import React, { useState } from 'react';
import { Box, Container, Flex, Text, IconButton, keyframes } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaHeart } from 'react-icons/fa';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

// Subtle heartbeat animation
const heartbeat = keyframes`
  0%, 100% { transform: scale(1); }
  25% { transform: scale(1.1); }
  50% { transform: scale(1); }
`;

// Shimmer effect for text
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const ValentinesBanner = ({ variant = 'top' }) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  // Top banner variant - slim, elegant bar
  if (variant === 'top') {
    return (
      <AnimatePresence>
        <MotionBox
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          bg="linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)"
          borderBottom="1px solid"
          borderColor="gray.700"
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
          }}
        >
          <Container maxW="container.xl" position="relative">
            <Flex
              align="center"
              justify="center"
              py={{ base: 2, md: 2.5 }}
              px={{ base: 8, md: 4 }}
              gap={{ base: 2, md: 3 }}
              position="relative"
            >
              {/* Decorative Heart - Left */}
              <Box
                as={FaHeart}
                color="#FF6B9D"
                fontSize={{ base: 'xs', md: 'sm' }}
                animation={`${heartbeat} 2s ease-in-out infinite`}
                display={{ base: 'none', sm: 'block' }}
              />

              {/* Main Text */}
              <Flex
                align="center"
                gap={{ base: 1.5, md: 2 }}
                flexWrap="wrap"
                justify="center"
              >
                <Text
                  fontSize={{ base: 'xs', sm: 'sm', md: 'md' }}
                  fontWeight="600"
                  color="white"
                  fontFamily="'Zain', sans-serif"
                  letterSpacing="0.5px"
                >
                  Valentine's Day Special
                </Text>
                
                <Box
                  as="span"
                  fontSize={{ base: 'xs', sm: 'sm', md: 'md' }}
                  fontWeight="700"
                  fontFamily="'Zain', sans-serif"
                  letterSpacing="0.5px"
                  bgGradient="linear(to-r, #FF6B9D, #FFC3A0)"
                  bgClip="text"
                  backgroundSize="200% auto"
                  animation={`${shimmer} 3s linear infinite`}
                  display="inline-block"
                >
                  Save up to 40%
                </Box>

                <Text
                  fontSize={{ base: 'xs', sm: 'sm', md: 'md' }}
                  fontWeight="400"
                  color="gray.300"
                  fontFamily="'Zain', sans-serif"
                  display={{ base: 'none', md: 'inline' }}
                >
                  on select items
                </Text>
              </Flex>

              {/* Decorative Heart - Right */}
              <Box
                as={FaHeart}
                color="#FF6B9D"
                fontSize={{ base: 'xs', md: 'sm' }}
                animation={`${heartbeat} 2s ease-in-out infinite 0.5s`}
                display={{ base: 'none', sm: 'block' }}
              />

              {/* Close Button */}
              <IconButton
                icon={<FaTimes />}
                size="xs"
                variant="ghost"
                color="gray.400"
                _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
                onClick={() => setIsVisible(false)}
                position="absolute"
                right={{ base: 2, md: 4 }}
                top="50%"
                transform="translateY(-50%)"
                aria-label="Close banner"
              />
            </Flex>
          </Container>
        </MotionBox>
      </AnimatePresence>
    );
  }

  // Floating badge variant - corner badge
  if (variant === 'badge') {
    return (
      <AnimatePresence>
        <MotionBox
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          position="fixed"
          top={{ base: '80px', md: '90px' }}
          right={{ base: 4, md: 6 }}
          zIndex={999}
        >
          <MotionFlex
            direction="column"
            align="center"
            justify="center"
            bg="linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
            borderRadius="full"
            w={{ base: '60px', md: '70px' }}
            h={{ base: '60px', md: '70px' }}
            boxShadow="0 4px 20px rgba(255, 107, 157, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)"
            position="relative"
            overflow="hidden"
            cursor="pointer"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsVisible(false)}
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: 'full',
              padding: '2px',
              background: 'linear-gradient(135deg, #FF6B9D, #FFC3A0)',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
            }}
          >
            <Box
              as={FaHeart}
              color="#FF6B9D"
              fontSize={{ base: 'sm', md: 'md' }}
              animation={`${heartbeat} 1.5s ease-in-out infinite`}
              mb={0.5}
            />
            <Text
              fontSize={{ base: '2xs', md: 'xs' }}
              fontWeight="700"
              color="white"
              fontFamily="'Zain', sans-serif"
              textAlign="center"
              lineHeight="1"
            >
              UP TO
            </Text>
            <Text
              fontSize={{ base: 'xs', md: 'sm' }}
              fontWeight="800"
              bgGradient="linear(to-r, #FF6B9D, #FFC3A0)"
              bgClip="text"
              fontFamily="'Zain', sans-serif"
              lineHeight="1"
            >
              40% OFF
            </Text>
          </MotionFlex>
        </MotionBox>
      </AnimatePresence>
    );
  }

  // Inline banner variant - for product pages
  if (variant === 'inline') {
    return (
      <AnimatePresence>
        <MotionBox
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -20, opacity: 0 }}
          transition={{ duration: 0.3 }}
          bg="linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)"
          borderRadius="xl"
          p={{ base: 3, md: 4 }}
          mb={{ base: 4, md: 6 }}
          position="relative"
          overflow="hidden"
          border="1px solid"
          borderColor="gray.700"
          boxShadow="0 4px 15px rgba(0, 0, 0, 0.2)"
        >
          {/* Gradient overlay */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="2px"
            bgGradient="linear(to-r, transparent, #FF6B9D, #FFC3A0, transparent)"
            opacity={0.8}
          />

          <Flex
            align="center"
            justify="space-between"
            gap={3}
          >
            <Flex align="center" gap={3} flex={1}>
              {/* Heart Icon */}
              <Flex
                align="center"
                justify="center"
                w={{ base: '40px', md: '50px' }}
                h={{ base: '40px', md: '50px' }}
                bg="linear-gradient(135deg, #FF6B9D, #FFC3A0)"
                borderRadius="full"
                flexShrink={0}
              >
                <Box
                  as={FaHeart}
                  color="white"
                  fontSize={{ base: 'lg', md: 'xl' }}
                  animation={`${heartbeat} 1.5s ease-in-out infinite`}
                />
              </Flex>

              {/* Text Content */}
              <Flex direction="column" gap={0.5}>
                <Text
                  fontSize={{ base: 'sm', md: 'md' }}
                  fontWeight="700"
                  color="white"
                  fontFamily="'Zain', sans-serif"
                  letterSpacing="0.5px"
                >
                  Valentine's Day Special Offer
                </Text>
                <Flex align="center" gap={2} flexWrap="wrap">
                  <Text
                    fontSize={{ base: 'xs', md: 'sm' }}
                    fontWeight="600"
                    bgGradient="linear(to-r, #FF6B9D, #FFC3A0)"
                    bgClip="text"
                    fontFamily="'Zain', sans-serif"
                  >
                    Get up to 60% OFF
                  </Text>
                  {/* <Text
                    fontSize={{ base: 'xs', md: 'sm' }}
                    color="gray.400"
                    fontFamily="'Zain', sans-serif"
                  >
                    Limited time only
                  </Text> */}
                </Flex>
              </Flex>
            </Flex>

            {/* Close Button */}
            <IconButton
              icon={<FaTimes />}
              size="sm"
              variant="ghost"
              color="gray.400"
              _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
              onClick={() => setIsVisible(false)}
              aria-label="Close banner"
              flexShrink={0}
            />
          </Flex>
        </MotionBox>
      </AnimatePresence>
    );
  }

  return null;
};

export default ValentinesBanner;
